import {DataTable} from "../DataTable/DataTable";
import {PipelineGraphEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {ActionIcon, Group} from "@mantine/core";
import {useNavigate} from "react-router-dom";
import {IconEdit, IconEye, IconTrash} from "@tabler/icons-react";
import {useCallback} from "react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {usePipelineGraphMutation} from "../../../hooks/usePipelineGraphMutation";
import {usePagination} from "../DataTable/usePagination";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: PipelineGraphEntity[] = []

export type PipelineGraphDataTableProps = EntityDataTableProps<PipelineGraphEntity>

export const PipelineGraphDataTable = (props: PipelineGraphDataTableProps) => {
  const {
    records: graphs = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: graphs,
    sortStatus: {columnAccessor: 'effectiveDttm', direction: 'desc'},
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    deletePipelineGraph
  } = usePipelineGraphMutation();

  const onViewClick = useCallback((pipelineGraph: PipelineGraphEntity) => {
    navigate(`graph?id=${pipelineGraph.id}`)
  }, [navigate])

  const onEditClick = useCallback((pipelineGraph: PipelineGraphEntity) => {
    navigate("graph-builder", {
      state: {
        pipelineId: pipelineGraph.pipelineId,
        pipelineGraphId: pipelineGraph.id,
      }
    });
  }, [navigate])

  const onDeleteClick = useCallback((record: PipelineGraphEntity) => {
    deletePipelineGraph(record.id, undefined)
  }, [deletePipelineGraph])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={[
        {
          title: t("table.entity.pipelinegraph.column.effectivedttm", "Effective Date"),
          accessor: "effectiveDttm",
          width: 100,
          render: ({effectiveDttm}) => (<FormattedDateTime date={effectiveDttm}/>)
        },
        {
          title: t("table.entity.pipelinegraph.column.name", "Name"),
          accessor: "displayName",
          width: 100,
        },
        {
          title: '',
          accessor: "actions",
          width: 100,
          textAlignment: 'right',
          render: (pipelineGraph) => (
            <HasAccess permissions={["edit"]}>
              <Group spacing={4} position="right" noWrap>
                <ActionIcon color="primary" onClick={() => onViewClick(pipelineGraph)}>
                  <IconEye size={16}/>
                </ActionIcon>
                <ActionIcon onClick={() => onEditClick(pipelineGraph)}>
                  <IconEdit size={16}/>
                </ActionIcon>
                <ActionIcon color="red" onClick={() => onDeleteClick(pipelineGraph)}>
                  <IconTrash size={16}/>
                </ActionIcon>
              </Group>
            </HasAccess>
          )
        }
      ]}
    />
  )
}
